import { find as _find, sortBy as _sortBy } from 'lodash-es';
import { useState, useMemo } from 'react';
import { useMutation } from '@apollo/client/react';
import Button from '@mui/material/Button';
import { makeStyles } from 'tss-react/mui';
import PauseCircleFilled from '@mui/icons-material/PauseCircleFilled';
import type { Group } from '../../types/Group';
import type { GroupInvite } from '../../types/Invite';
import { useAppContext } from '../../contexts/app';
import { joinGroupMutation, myGroupsQuery } from '../../apollo/groups';
import { meQuery } from '../../apollo/auth';
import RequestJoinButton from './request-join-button';
import MaterialLinkButton from '../material-link-button';

const useStyles = makeStyles()((theme) => ({
  joinMessage: {
    display: 'flex',
    alignItems: 'center',

    // important for when this is included in a <h2>
    fontSize: '0.875rem',
    fontWeight: 'normal',
  },
  joinIcon: {
    marginRight: theme.spacing(0.5),
  },
}));

type Props = {
  group?: Group;
  onJoin?: (...args: unknown[]) => void;
  returnTo?: string;
  groupInvite?: GroupInvite;
  [key: string]: unknown;
};

const GroupJoinButton = ({
  group,
  groupInvite,
  onJoin,
  returnTo = null,
  ...rest
}: Props) => {
  const [submitting, setSubmitting] = useState(false);
  const { login, loggedInUser, isLoggedIn } = useAppContext();
  const { classes } = useStyles();

  const [joinGroup] = useMutation(joinGroupMutation, {
    update: (proxy, { data: { joinGroup: groupMember } }) => {
      const data = proxy.readQuery({
        query: meQuery
      });
      proxy.writeQuery({
        query: meQuery,
        data: {
          ...data,
          me: {
            ...data.me,
            groupMembers: [...data.me.groupMembers, groupMember],
          }
        }
      });

      try {
        const data = proxy.readQuery({
          query: myGroupsQuery
        });
        proxy.writeQuery({
          query: myGroupsQuery,
          data: {
            ...data,
            me: {
              ...data.me,
              groups: _sortBy([...data.me.groups, group], 'name'),
            }
          }
        });
      } catch {
        // do nothing
      }
    }
  });

  const membership = useMemo(() => {
    if (!loggedInUser || group.found) {
      return null;
    }
    return _find(loggedInUser.groupMembers, ({ group: { id: groupId } }) => (
      groupId === group.id
    ));
  }, [loggedInUser, group]);

  if (group.found) {
    return null;
  }

  if (membership) {
    if (membership.role === 'REQUESTED') {
      return (
        <div className={classes.joinMessage}>
          <PauseCircleFilled className={classes.joinIcon} />
          <strong>Membership request is pending</strong>
        </div>
      );
    } else {
      return (
        <MaterialLinkButton
          href={`/groups/${group.slug}/settings`}
          variant="outlined"
          title="Edit Membership Settings"
          {...rest}
        >
          Joined
        </MaterialLinkButton>
      );
    }
  }

  if (group.public) {
    if (!isLoggedIn) {
      return (
        <Button
          variant="contained"
          color="primary"
          onClick={(e) => {
            e.preventDefault();
            setSubmitting(true);
            login(returnTo || `/groups/${group.slug}`);
          }}
          disabled={submitting}
          {...rest}
        >
          Join Group
        </Button>
      );
    }

    return (
      <Button
        variant="contained"
        color="primary"
        onClick={(e) => {
          e.preventDefault();
          setSubmitting(true);
          joinGroup({
            variables: { id: group.id }
          }).then(() => {
            setSubmitting(false);
            if (onJoin) {
              onJoin();
            }
          });
        }}
        disabled={submitting}
        {...rest}
      >
        Join Group
      </Button>
    );
  }

  if (groupInvite) {
    if (!isLoggedIn) {
      return (
        <Button
          variant="contained"
          color="primary"
          onClick={(e) => {
            e.preventDefault();
            setSubmitting(true);
            login(`/groups/${group.slug}?join=${groupInvite.id}`);
          }}
          disabled={submitting}
          {...rest}
        >
          Join Group
        </Button>
      );
    }

    return (
      <Button
        color="primary"
        variant="contained"
        onClick={(e) => {
          e.preventDefault();
          setSubmitting(true);
          joinGroup({
            variables: {
              id: group.id,
              token: groupInvite.id
            }
          }).then(() => {
            setSubmitting(false);
            if (onJoin) {
              onJoin();
            }
          });
        }}
        disabled={submitting}
        {...rest}
      >
        Join Group
      </Button>
    );
  }

  return (
    <RequestJoinButton
      group={group}
      onJoin={onJoin}
      returnTo={returnTo}
      {...rest}
    />
  );
};

export default GroupJoinButton;
